import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, X, type LucideIcon } from 'lucide-react';
import Popover from './Popover';
import StateBlock from './StateBlock';
import {
  edgeActive, filterOptions, moveActive, toggleValue, withParentPaths, EMPTY_VALUE, type PickerOption,
} from './entityPickerModel';

export type { PickerOption } from './entityPickerModel';

/** Strings a caller may override (CustomerPicker keeps its own wording). */
export interface EntityPickerTexts {
  placeholder?: string;
  clear?: (name: string) => string;
  toggle?: string;
  loading?: string;
  loadError?: string;
  noMatches?: string;
  searching?: string;
  /** Shown for a stored value that is not in the list. */
  unknown?: string;
  more?: (count: number) => string;
}

interface BaseProps {
  /** Control id (a `Field` injects it, so its label points here). */
  id?: string;
  options: readonly PickerOption[];
  /** Hint shown when nothing is chosen. */
  placeholder?: string;
  /**
   * A real "none / all" choice (value ''), shown as the first row, e.g.
   * "ללא אבן דרך" or "כל הלקוחות". Without it the empty value is only a prompt.
   */
  emptyLabel?: string;
  /** Offer a clear button when something is chosen (default: single picker yes; multiple only with `emptyLabel`). */
  clearable?: boolean;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  /** `Field` appends `input-error` to the className of its control. */
  className?: string;
  'aria-label'?: string;
  /** Id of a visible label element (names the input and the list). */
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-required'?: boolean | 'true' | 'false';
  onBlur?: () => void;
  /** Leading icon in the field. */
  icon?: LucideIcon;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  retrying?: boolean;
  /** The typed text, e.g. to search on the server when the list is truncated. */
  onQueryChange?: (query: string) => void;
  /** A server search is running. */
  searching?: boolean;
  /** Label of a stored value that is not in `options` (shown instead of "unknown"). */
  currentLabel?: string;
  texts?: EntityPickerTexts;
  /** Max rows shown at once. */
  limit?: number;
  /** Also submit the value with a native form (`<input type=hidden name>`). */
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  /** `data-*` attributes for the input (e.g. a form's "first error" probe). */
  dataAttrs?: Record<`data-${string}`, string | undefined>;
}

export interface SinglePickerProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string, option: PickerOption | null) => void;
}
export interface MultiPickerProps extends BaseProps {
  multiple: true;
  values: readonly string[];
  onValuesChange: (values: string[]) => void;
}
export type EntityPickerProps = SinglePickerProps | MultiPickerProps;

const DEFAULT_LIMIT = 200;

/**
 * Searchable picker for names (customer, employee, contact, engagement,
 * project, milestone, task, folder) in forms and filters (P-41, D-203/D-207).
 * The look and behaviour of `CustomerPicker`: a field with an icon and chevron,
 * a rounded floating list in `ui/Popover`, type to search, a grey second line,
 * the chosen row bold with a check. ARIA 1.2 combobox: ↑/↓/Home/End move,
 * Enter picks (never submits the form), Escape closes. Single or multiple.
 * Short fixed lists (status, priority, yes/no) stay a plain `<select>`.
 */
export default function EntityPicker(props: EntityPickerProps) {
  const {
    id, options, placeholder, emptyLabel, disabled = false, required = false, invalid = false, className = '',
    icon: Icon, loading = false, error = false, onRetry, retrying = false, onQueryChange, searching = false,
    currentLabel, texts, limit = DEFAULT_LIMIT, name, onBlur, inputRef: extRef,
  } = props;
  const { t } = useTranslation();
  const baseId = useId();
  const inputId = id ?? `${baseId}-input`;
  const listId = `${baseId}-list`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;
  const anchorRef = useRef<HTMLDivElement>(null);
  const ownInputRef = useRef<HTMLInputElement>(null);
  const setInputRef = (el: HTMLInputElement | null) => {
    (ownInputRef as { current: HTMLInputElement | null }).current = el;
    if (typeof extRef === 'function') extRef(el);
    else if (extRef) (extRef as { current: HTMLInputElement | null }).current = el;
  };

  const multiple = props.multiple === true;
  const values: readonly string[] = multiple ? props.values : props.value ? [props.value] : [];
  const single = multiple ? '' : props.value;
  const isInvalid = invalid || props['aria-invalid'] === true || props['aria-invalid'] === 'true' || /\binput-error\b/.test(className);
  const isRequired = required || props['aria-required'] === true || props['aria-required'] === 'true';
  // The old <select> let you go back to the empty option, so a single picker is clearable by default.
  const clearable = props.clearable ?? (multiple ? Boolean(emptyLabel) : true);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const [active, setActive] = useState(0);

  const setQ = (q: string) => { setQuery(q); onQueryChange?.(q); };

  const withEmpty = useMemo<PickerOption[]>(
    () => (emptyLabel ? [{ value: EMPTY_VALUE, label: emptyLabel }, ...options] : [...options]),
    [options, emptyLabel],
  );
  // The "none" row only makes sense for the single picker and an empty search.
  const { rows, total } = useMemo(() => {
    const searching_ = editing && query.trim() !== '';
    const src = searching_ || multiple ? options : withEmpty;
    const r = filterOptions(src, editing ? query : '', limit);
    return searching_ ? { rows: withParentPaths(r.rows, options), total: r.total } : r;
  }, [options, withEmpty, editing, query, limit, multiple]);

  useEffect(() => {
    if (active >= rows.length) setActive(Math.max(0, rows.length - 1));
  }, [active, rows.length]);
  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
    // optionId derives from the stable baseId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const selectedOpt = single ? options.find((o) => o.value === single) ?? null : null;
  const emptyShown = !multiple && !single && emptyLabel ? emptyLabel : '';
  const unknownText = currentLabel ?? texts?.unknown ?? t('entityPicker.unknown');
  const shownText = editing
    ? query
    : multiple
      ? ''
      : selectedOpt?.label ?? (single ? (loading ? '' : unknownText) : emptyShown);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    if (!editing) {
      setEditing(true);
      setQ('');
      const all = multiple ? options : withEmpty;
      const i = single ? all.findIndex((o) => o.value === single) : 0;
      setActive(Math.max(0, i));
    }
  };
  const close = () => { setOpen(false); setEditing(false); setQ(''); };

  const pick = (o: PickerOption) => {
    if (o.disabled) return;
    if (props.multiple) {
      props.onValuesChange(toggleValue(props.values, o.value));
      setQ('');
      ownInputRef.current?.focus();
      return;
    }
    props.onChange(o.value, o.value === EMPTY_VALUE ? null : o);
    close();
    ownInputRef.current?.focus();
  };
  const clear = () => {
    if (props.multiple) props.onValuesChange([]);
    else props.onChange(EMPTY_VALUE, null);
    close();
    ownInputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { openList(); return; }
        setActive((a) => moveActive(rows, a, 1));
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) { openList(); return; }
        setActive((a) => moveActive(rows, a, -1));
        return;
      case 'Home':
      case 'End':
        if (!open || rows.length === 0) return;
        e.preventDefault();
        setActive(edgeActive(rows, e.key === 'End'));
        return;
      case 'Enter':
        // Inside an open list Enter picks; it never submits the form.
        if (!open) return;
        e.preventDefault();
        if (rows[active]) pick(rows[active]);
        return;
      case 'Escape':
        if (open || editing) { e.preventDefault(); e.stopPropagation(); close(); }
        return;
      case 'Backspace':
        if (multiple && !query && props.multiple && props.values.length) {
          props.onValuesChange(props.values.slice(0, -1));
        }
        return;
      case 'Tab':
        close();
        return;
      default:
    }
  };

  const rowFromEvent = (e: MouseEvent<HTMLElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-index]');
    return el ? Number(el.dataset.index) : -1;
  };

  const tx = {
    placeholder: placeholder ?? texts?.placeholder ?? t('entityPicker.placeholder'),
    toggle: texts?.toggle ?? t('entityPicker.toggle'),
    loading: texts?.loading ?? t('entityPicker.loading'),
    loadError: texts?.loadError ?? t('entityPicker.loadError'),
    noMatches: texts?.noMatches ?? t('entityPicker.noMatches'),
    searching: texts?.searching ?? t('entityPicker.searching'),
    clear: (n: string) => (texts?.clear ? texts.clear(n) : t('entityPicker.clear', { name: n })),
    more: (n: number) => (texts?.more ? texts.more(n) : t('entityPicker.more', { count: n })),
  };
  const listLabel = props['aria-label'] ?? tx.placeholder;
  const hasValue = values.length > 0;
  const listRendered = open && !loading && !(error && options.length === 0);
  const chips: ReactNode = multiple && hasValue
    ? values.map((v) => {
        const label = options.find((o) => o.value === v)?.label ?? unknownText;
        return (
          <span key={v} className="inline-flex max-w-full items-center gap-1 rounded-md bg-surface-subtle px-1.5 py-0.5 text-xs text-text">
            <bdi className="truncate">{label}</bdi>
            {!disabled && (
              <button
                type="button"
                tabIndex={-1}
                onClick={() => { if (props.multiple) props.onValuesChange(props.values.filter((x) => x !== v)); }}
                aria-label={tx.clear(label)}
                className="rounded text-text-2 hover:text-text"
              >
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </span>
        );
      })
    : null;

  return (
    <div className={className.replace(/\binput-error\b/, '').trim() || undefined}>
      <div
        ref={anchorRef}
        className={`relative flex min-h-10 w-full flex-wrap items-center gap-1 rounded-lg border bg-surface transition-[border-color,box-shadow] duration-150 focus-within:border-focus focus-within:ring-1 focus-within:ring-focus ${isInvalid ? 'border-danger-600' : 'border-control hover:border-surface-600'} ${disabled ? 'opacity-60' : ''}`}
      >
        {Icon && <Icon size={16} className="pointer-events-none absolute start-3 text-text-3" aria-hidden="true" />}
        {chips && <span className={`flex flex-wrap gap-1 ${Icon ? 'ps-9' : 'ps-2'} py-1`}>{chips}</span>}
        <input
          ref={setInputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open && listRendered ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && rows[active] ? optionId(active) : undefined}
          aria-required={isRequired || undefined}
          aria-invalid={isInvalid || undefined}
          aria-describedby={props['aria-describedby']}
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          value={shownText}
          placeholder={hasValue ? undefined : tx.placeholder}
          onChange={(e) => { setEditing(true); setQ(e.target.value); setOpen(true); setActive(0); }}
          onClick={openList}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          {...props.dataAttrs}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          dir="auto"
          className={`min-w-0 flex-1 bg-transparent py-2 pe-1 text-sm text-text placeholder:text-text-3 focus:outline-none ${chips ? 'ps-2' : Icon ? 'ps-9' : 'ps-3'}`}
        />
        {name && !multiple && <input type="hidden" name={name} value={single} />}
        {clearable && hasValue && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            aria-label={tx.clear(selectedOpt?.label ?? '')}
            className="touch-target flex size-8 flex-shrink-0 items-center justify-center rounded-md text-text-2 hover:bg-surface-subtle hover:text-text"
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { if (open) close(); else { openList(); ownInputRef.current?.focus(); } }}
          aria-label={tx.toggle}
          aria-expanded={open}
          aria-controls={listRendered ? listId : undefined}
          disabled={disabled}
          className="me-1 flex size-8 flex-shrink-0 items-center justify-center rounded-md text-text-2 hover:bg-surface-subtle hover:text-text"
        >
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] p-1.5"
      >
        {loading ? (
          <StateBlock kind="loading" size="inline" title={tx.loading} className="px-2.5 py-3" />
        ) : error && options.length === 0 ? (
          <StateBlock
            kind="error"
            size="inline"
            title={tx.loadError}
            description={null}
            onRetry={onRetry}
            retrying={retrying}
            className="px-2.5 py-2"
          />
        ) : (
          <>
            <ul
              id={listId}
              role="listbox"
              aria-label={props['aria-labelledby'] ? undefined : listLabel}
              aria-labelledby={props['aria-labelledby']}
              aria-multiselectable={multiple || undefined}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => { const i = rowFromEvent(e); if (i >= 0 && rows[i]) pick(rows[i]); }}
              onMouseMove={(e) => { const i = rowFromEvent(e); if (i >= 0 && !rows[i]?.disabled) setActive(i); }}
              onKeyDown={onKeyDown}
              className="max-h-72 overflow-y-auto overscroll-contain focus:outline-none"
            >
              {rows.map((o, i) => {
                const isSel = values.includes(o.value) || (!multiple && o.value === EMPTY_VALUE && !single && Boolean(emptyLabel));
                const current = i === active;
                const showGroup = o.group && o.group !== rows[i - 1]?.group;
                return (
                  <li key={`${o.value}:${i}`} role="presentation">
                    {showGroup && (
                      <div className="px-2.5 pb-0.5 pt-2 text-xs font-medium text-text-2" aria-hidden="true">{o.group}</div>
                    )}
                    <div
                      id={optionId(i)}
                      role="option"
                      aria-selected={isSel}
                      aria-disabled={o.disabled || undefined}
                      data-index={i}
                      className={`flex cursor-default items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm ${o.disabled ? 'opacity-50' : ''} ${current ? 'bg-accent-soft text-accent-soft-text' : 'text-text'}`}
                      style={o.depth ? { paddingInlineStart: `${0.625 + o.depth * 1.25}rem` } : undefined}
                    >
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate ${isSel ? 'font-medium' : ''}`}><bdi>{o.label}</bdi></span>
                        {o.secondary && (
                          <span className={`block truncate text-xs ${current ? 'text-accent-soft-text' : 'text-text-2'}`}><bdi>{o.secondary}</bdi></span>
                        )}
                      </span>
                      {isSel && <Check size={16} className="flex-shrink-0" aria-hidden="true" />}
                    </div>
                  </li>
                );
              })}
            </ul>
            {rows.length === 0 && (
              <p className="px-2.5 py-3 text-sm text-text-2">{searching ? tx.searching : tx.noMatches}</p>
            )}
            {total > rows.length && (
              <p className="border-t border-border px-2.5 pb-1 pt-2 text-xs text-text-2" aria-live="polite">
                {tx.more(total - rows.length)}
              </p>
            )}
          </>
        )}
      </Popover>
    </div>
  );
}
